import { useEffect, useState, type FormEvent } from 'react'
import type { DirectoryEntryView, StaffMember, StaffRole } from '@fitting-room/contracts'
import { CalendarMarkPicker } from '../../../components/admin/CalendarMarkPicker'
import { useAddTeamMember, useSetTeamMemberActive, useTeam, useUpdateTeamMember } from '../../../lib/adminQueries'
import { bpsFromPercent, percentFromBps } from '../../../lib/backOffice'
import { useStaffToken } from '../../../lib/staffToken'

const ROLE_LABEL: Record<StaffRole, string> = { owner: 'Owner', staff: 'Staff', marketer: 'Marketer' }

/**
 * Settings → Team: who can sign in to the console with Google, and as what.
 * Ported from tperent's Members tab — one row per person, every control on
 * the row saving as it changes, the way a list of people is edited in
 * practice: one fix at a time, not a form to fill in and submit.
 *
 * The deployment's own directory is shown beside it, read-only. It is how the
 * first owner got in, and it wins over the team for any address it names, so
 * the screen says so on the rows where that is true rather than offering a
 * role control that would do nothing.
 */
export function TeamSection() {
  const { token } = useStaffToken()
  const team = useTeam(token)
  // Deactivated people are kept — their appointments and orders still name
  // them — but they are not the team anyone is working with, so they stay
  // out of the way until asked for.
  const [showDeactivated, setShowDeactivated] = useState(false)

  if (team.isPending) return <p className="st-meta">Loading the team…</p>
  if (team.isError) return <p className="st-alert">Could not load the team. Try again in a moment.</p>

  const { members, directory } = team.data
  const onTeam = new Set(members.map((m) => m.email))
  const directoryOnly = directory.filter((entry) => !onTeam.has(entry.email))
  const deactivated = members.filter((m) => !m.active).length
  const shown = showDeactivated ? members : members.filter((m) => m.active)

  return (
    <section className="mb-10">
      <h2 className="st-display mb-1 text-xl">Team</h2>
      <p className="st-meta mb-4 max-w-2xl">
        Who can sign in to this console with Google, and as what. Staff work appointments, inquiries and the catalog;
        owners also get syncing, publishing and these settings. Deactivating someone signs them out on their next
        click — the appointments and orders they handled stay theirs.
      </p>

      <AddMemberForm staffToken={token} />

      {members.length === 0 ? (
        <p className="st-meta mb-4">Nobody has been added yet.</p>
      ) : (
        <>
          <p className="st-meta mb-3 max-w-2xl text-xs">
            Calendar mark: each person picks one, and once it&rsquo;s taken nobody else can. Don&rsquo;t see one you
            like? Choose &ldquo;Custom…&rdquo; and paste any emoji. Appointments on the calendar show the mark of
            whoever they&rsquo;re assigned to.
          </p>
          <ul className="mb-3 flex flex-col gap-2" aria-label="Team members">
            {shown.map((member) => (
              <MemberRow
                key={member.id}
                staffToken={token}
                member={member}
                team={members}
                listed={directory.find((entry) => entry.email === member.email)}
              />
            ))}
          </ul>
          {deactivated > 0 ? (
            <label className="st-meta mb-6 flex items-center gap-2 text-sm">
              <input type="checkbox" checked={showDeactivated} onChange={(event) => setShowDeactivated(event.target.checked)} />
              Show deactivated ({deactivated})
            </label>
          ) : (
            <div className="mb-3" />
          )}
        </>
      )}

      {directory.length > 0 ? <DirectoryList staffToken={token} entries={directoryOnly} total={directory.length} /> : null}
    </section>
  )
}

function AddMemberForm({ staffToken }: { readonly staffToken: string | null }) {
  const add = useAddTeamMember(staffToken)
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<StaffRole>('staff')

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (!email.trim()) return
    add.mutate({ email: email.trim(), role }, { onSuccess: () => setEmail('') })
  }

  return (
    <form onSubmit={submit} className="mb-4 flex max-w-2xl flex-wrap items-end gap-3">
      <label className="flex min-w-64 flex-1 flex-col gap-1">
        <span className="st-eyebrow">Google account email</span>
        <input
          className="st-input"
          type="email"
          autoComplete="off"
          placeholder="name@example.com"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
      </label>
      <label className="flex flex-col gap-1">
        <span className="st-eyebrow">Role</span>
        <select className="st-select" value={role} onChange={(event) => setRole(event.target.value as StaffRole)}>
          <option value="staff">{ROLE_LABEL.staff}</option>
          <option value="owner">{ROLE_LABEL.owner}</option>
          <option value="marketer">{ROLE_LABEL.marketer}</option>
        </select>
      </label>
      <button type="submit" className="st-btn" disabled={add.isPending || !email.trim()}>
        {add.isPending ? 'Adding…' : 'Add to team'}
      </button>
      {add.isError ? (
        <p className="st-alert w-full" role="alert">
          {add.error.message}
        </p>
      ) : null}
    </form>
  )
}

function MemberRow({
  staffToken,
  member,
  team,
  listed,
}: {
  readonly staffToken: string | null
  readonly member: StaffMember
  readonly team: readonly StaffMember[]
  readonly listed: DirectoryEntryView | undefined
}) {
  const update = useUpdateTeamMember(staffToken, member.id)
  const setActive = useSetTeamMemberActive(staffToken, member.id)
  const [name, setName] = useState(member.displayName)
  const [rate, setRate] = useState(percentFromBps(member.commissionRateBps))
  const [rateInvalid, setRateInvalid] = useState(false)

  // What the server holds wins whenever it changes — after a save, or another
  // owner's edit arriving on refetch.
  useEffect(() => setName(member.displayName), [member.displayName])
  useEffect(() => setRate(percentFromBps(member.commissionRateBps)), [member.commissionRateBps])

  const commitName = () => {
    const trimmed = name.trim()
    if (!trimmed) return setName(member.displayName)
    if (trimmed !== member.displayName) update.mutate({ displayName: trimmed })
  }

  const commitRate = () => {
    const bps = bpsFromPercent(rate)
    if (bps === undefined) return setRateInvalid(true)
    setRateInvalid(false)
    if (bps !== member.commissionRateBps) update.mutate({ commissionRateBps: bps })
  }

  const error = update.error ?? setActive.error

  return (
    <li className="st-card px-4 py-3" data-member-id={member.id} style={member.active ? undefined : { opacity: 0.7 }}>
      <div className="flex flex-wrap items-start gap-4">
        <div className="flex min-w-56 flex-1 flex-col gap-1">
          <input
            className="st-input"
            aria-label={`Name for ${member.email}`}
            value={name}
            onChange={(event) => setName(event.target.value)}
            onBlur={commitName}
            onKeyDown={(event) => event.key === 'Enter' && (event.currentTarget as HTMLInputElement).blur()}
          />
          <span className="st-meta text-xs">
            {member.email}
            {member.active ? '' : ' · Deactivated'}
          </span>
        </div>

        <label className="flex flex-col gap-1">
          <span className="st-eyebrow">Role</span>
          {listed ? (
            <span className="st-meta py-1.5" title="Set in the deployment's staff directory">
              {ROLE_LABEL[listed.role]} · set by the deployment
            </span>
          ) : (
            <select
              className="st-select"
              value={member.role}
              disabled={update.isPending}
              onChange={(event) => update.mutate({ role: event.target.value as StaffRole })}
            >
              <option value="staff">{ROLE_LABEL.staff}</option>
              <option value="owner">{ROLE_LABEL.owner}</option>
              <option value="marketer">{ROLE_LABEL.marketer}</option>
            </select>
          )}
        </label>

        <div className="flex flex-col gap-1">
          <span className="st-eyebrow">Calendar mark</span>
          <CalendarMarkPicker staffToken={staffToken} member={member} team={team} />
        </div>

        <label className="flex w-32 flex-col gap-1">
          <span className="st-eyebrow">Commission %</span>
          <input
            className="st-input"
            inputMode="decimal"
            placeholder="None"
            value={rate}
            onChange={(event) => {
              setRate(event.target.value)
              setRateInvalid(false)
            }}
            onBlur={commitRate}
            onKeyDown={(event) => event.key === 'Enter' && (event.currentTarget as HTMLInputElement).blur()}
          />
        </label>

        <div className="ml-auto self-center">
          <button
            type="button"
            className="st-btn-quiet"
            disabled={setActive.isPending}
            onClick={() => setActive.mutate(!member.active)}
          >
            {member.active ? 'Deactivate' : 'Reactivate'}
          </button>
        </div>
      </div>

      {listed && !member.active ? (
        <p className="st-meta mt-2 text-xs">
          Still signs in: the deployment&rsquo;s staff directory lists this address, and it wins over the team.
        </p>
      ) : null}
      {rateInvalid ? (
        <p className="st-alert mt-2 text-xs" role="alert">
          Enter a percentage from 0 to 100, or leave it blank for no commission.
        </p>
      ) : null}
      {error ? (
        <p className="st-alert mt-2 text-xs" role="alert">
          {error.message}
        </p>
      ) : null}
    </li>
  )
}

/**
 * The deployment's directory, for the addresses that have no row on the team.
 * Read-only: it lives in the deployment's configuration, not in this database.
 * Adding one to the team gives it a name, a calendar mark and a commission
 * rate — the directory still decides whether it signs in and as what.
 */
function DirectoryList({
  staffToken,
  entries,
  total,
}: {
  readonly staffToken: string | null
  readonly entries: readonly DirectoryEntryView[]
  readonly total: number
}) {
  const add = useAddTeamMember(staffToken)
  return (
    <div className="max-w-2xl">
      <h3 className="st-eyebrow mb-1">From the deployment</h3>
      <p className="st-meta mb-3 text-xs">
        {total === 1 ? 'One address signs in' : `${total} addresses sign in`} through the deployment&rsquo;s staff
        directory, which can&rsquo;t be changed here. Add one to the team to give them a calendar mark or a commission
        rate.
      </p>
      {entries.length > 0 ? (
        <table className="st-table">
          <tbody>
            {entries.map((entry) => (
              <tr key={entry.email}>
                <td>{entry.email}</td>
                <td>{ROLE_LABEL[entry.role]}</td>
                <td className="text-right">
                  <button
                    type="button"
                    className="st-btn-ghost"
                    disabled={add.isPending}
                    onClick={() => add.mutate({ email: entry.email, role: entry.role })}
                  >
                    Add to team
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
      {add.isError ? (
        <p className="st-alert mt-2" role="alert">
          {add.error.message}
        </p>
      ) : null}
    </div>
  )
}
